import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogContent,
    Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../theme';
import CookieSyncOnboarding from './CookieSyncOnboarding';
import ApiOnboarding from './ApiOnboarding';
import CBOnboarding from './CBOnboarding';
import EnableReleaseProductDialog from './EnableReleaseProductDialog';
import ManageReleaseProduct from './ManageReleaseProduct';

// 1) RTK Query import
import { useGetDataAssetsQuery } from '../../../features/data_assets/dataAssetsApiSlice';
import { useNavigate } from 'react-router-dom';

const ProductOverview = ({ organization }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [localProducts, setLocalProducts] = useState(() => organization?.products || []);

    useEffect(() => {
        setLocalProducts(Array.isArray(organization?.products) ? organization.products : []);
    }, [organization?.products]);

    // Control for the Cookie Sync dialog
    const [openDialog, setOpenDialog] = useState(false);
    const handleDialogOpen = () => setOpenDialog(true);
    const handleDialogClose = () => setOpenDialog(false);

    const [openApiDialog, setOpenApiDialog] = useState(false);
    const openApi = () => setOpenApiDialog(true);
    const closeApi = () => setOpenApiDialog(false);

    const [openCbDialog, setOpenCbDialog] = useState(false);
    const openCb = () => setOpenCbDialog(true);
    const closeCb = () => setOpenCbDialog(false);

    const [releaseProductDialogOpen, setReleaseProductDialogOpen] = useState(false);
    const [selectedReleaseAsset, setSelectedReleaseAsset] = useState(null);

    const [manageReleaseProductOpen, setManageReleaseProductOpen] = useState(false);
    const [selectedManageAsset, setSelectedManageAsset] = useState(null);
    const [selectedManageProductName, setSelectedManageProductName] = useState('');

    const openManageReleaseProduct = ({ asset, productName }) => {
        setSelectedManageAsset(asset || null);
        setSelectedManageProductName(productName || asset?.name || '');
        setManageReleaseProductOpen(true);
    };

    const closeManageReleaseProduct = () => {
        setManageReleaseProductOpen(false);
        setSelectedManageAsset(null);
        setSelectedManageProductName('');
    };

    const openReleaseProductDialog = (asset) => {
        setSelectedReleaseAsset(asset);
        setReleaseProductDialogOpen(true);
    };

    const closeReleaseProductDialog = () => {
        setReleaseProductDialogOpen(false);
        setSelectedReleaseAsset(null);
    };

    // We'll say an org "has ADC" if it includes "ADC Resolution Process" in its products
    const hasADC = localProducts.includes("ADC Resolution Process");

    // 2) Fetch normalized data assets (RTK Query)
    const {
        data: dataAssetsResponse,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetDataAssetsQuery(undefined, {
        refetchOnFocus: false,
        refetchOnReconnect: false,
    });

    const allDataAssets = useMemo(() => {
        const { ids = [], entities = {} } = dataAssetsResponse || {};
        return ids.map((id) => entities[id]).filter(Boolean);
    }, [dataAssetsResponse]);

    const dataAssetByName = useMemo(() => {
        const map = new Map();

        for (const asset of allDataAssets) {
            if (asset?.name) {
                map.set(asset.name, asset);
            }
        }

        return map;
    }, [allDataAssets]);

    const isReleaseAsset = (asset) => {
        return asset?.release === 'Digital' || asset?.release === 'Contact';
    };

    const addLocalProduct = (productName) => {
        if (!productName) return;

        setLocalProducts((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            if (safePrev.includes(productName)) return safePrev;
            return [...safePrev, productName];
        });
    };

    const removeLocalProduct = (productName) => {
        if (!productName) return;

        setLocalProducts((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            return safePrev.filter((product) => product !== productName);
        });
    };

    /**
     * Example function: handle “Manage” a product already in org’s products
     * (In reality, you might show a modal or navigate to a config page.)
     */
    const navigate = useNavigate();
    const handleManageProduct = (productName) => {
        const orgId = organization?.organization_id;
        const asset = dataAssetByName.get(productName);

        if (isReleaseAsset(asset)) {
            openManageReleaseProduct({ asset, productName });
            return;
        }

        if (productName === 'ADC Resolution Process') {
            navigate(`/console/organization/${orgId}?tab=ADC`);
            return;
        }

        if (productName === 'APIs') {
            navigate(`/console/organization/${orgId}?tab=APIs`);
            return;
        }

        console.log(`Managing product: ${productName} for ${organization.name}`);
    };


    /**
     * handle “Enable” a data asset for the organization
     */
    const handleEnableAsset = (asset) => {
        const assetName = asset?.name;

        if (asset?.release === 'Digital' || asset?.release === 'Contact') {
            openReleaseProductDialog(asset);
            return;
        }

        if (assetName === "ADC Resolution Process") {
            handleDialogOpen();
        } else if (assetName === "APIs") {
            openApi();
        } else if (assetName === "Campaign Builder") {
            openCb();
        }
    };

    const handleConfirmEnableReleaseProduct = (asset) => {
        addLocalProduct(asset?.name);
        closeReleaseProductDialog();
    };
    const handleDisabledReleaseProduct = (productName) => {
        removeLocalProduct(productName);
        closeManageReleaseProduct();
    };

    // Predefine assets that are allowed to be enabled from the UI
    const CLICKABLE_ASSETS = ["ADC Resolution Process", "APIs", "Campaign Builder"]; //"Market Pulse 2.0", "APIs" "Campaign Builder"

    // Render the organization's current products (Left Column)
    const renderCurrentProducts = () => {
        const products = localProducts;

        return (
            <>
                <Typography variant="h4" mb={2} color="textPrimary">
                    Current Products
                </Typography>

                {products?.length > 0 ? (
                    products.map((product) => {
                        const asset = dataAssetByName.get(product);
                        const releaseProduct = isReleaseAsset(asset);

                        return (
                            <Box
                                key={product}
                                sx={{
                                    border: '1px solid white',
                                    borderRadius: 1,
                                    p: 1,
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                    <Typography variant="body1">{product}</Typography>

                                    {releaseProduct ? (
                                        <Chip
                                            size="small"
                                            label={asset.release}
                                            sx={{
                                                bgcolor: colors.blueAccent[700],
                                                color: colors.grey[100],
                                            }}
                                        />
                                    ) : null}
                                </Box>

                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ ml: 2 }}
                                    onClick={() => handleManageProduct(product)}
                                >
                                    Manage
                                </Button>
                            </Box>
                        );
                    })
                ) : (
                    <Typography variant="body1" color="textSecondary">
                        Member Has No Products
                    </Typography>
                )}

            </>
        );
    };

    // Render the external data assets (Right Column), excluding assets the org already has
    const renderAvailableAssets = () => {
        const hasLoadedAssets = allDataAssets.length > 0;

        if (isLoading && !hasLoadedAssets) {
            return <Typography>Loading data assets...</Typography>;
        }

        // Important: only hard-fail if we have no cached/loaded data.
        // If a later refetch fails, keep showing the last good data.
        if (isError && !hasLoadedAssets) {
            return <Typography color="error">Error loading data assets.</Typography>;
        }

        if (!dataAssetsResponse && !hasLoadedAssets) {
            return <Typography>No data assets found.</Typography>;
        }

        if (!hasLoadedAssets) {
            return <Typography>No data assets found.</Typography>;
        }

        const externalAssets = allDataAssets.filter(
            (asset) => asset?.usage === 'External'
        );

        const assetsNotOwned = externalAssets.filter(
            (asset) => asset?.name && !localProducts.includes(asset.name)
        );

        const filteredAssets = assetsNotOwned.filter((a) =>
            a.name !== 'ADC' &&
            a.name !== 'ADC Resolution Process - Postgres'
        );

        const sortedAssets = [...filteredAssets].sort((a, b) => {
            const aClick = CLICKABLE_ASSETS.includes(a.name);
            const bClick = CLICKABLE_ASSETS.includes(b.name);
            if (aClick && !bClick) return -1;
            if (!aClick && bClick) return 1;
            return a.name.localeCompare(b.name);
        });

        return (
            <>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="h5">
                        Available Products
                    </Typography>

                    {isError && hasLoadedAssets ? (
                        <Chip
                            size="small"
                            color="warning"
                            label="Using cached data"
                        />
                    ) : null}

                    {isFetching && hasLoadedAssets ? (
                        <Chip
                            size="small"
                            label="Refreshing..."
                        />
                    ) : null}
                </Box>

                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {sortedAssets.map((asset) => {
                        const isReleaseProduct = asset.release === 'Digital' || asset.release === 'Contact';
                        const isClickable = CLICKABLE_ASSETS.includes(asset.name) || isReleaseProduct;

                        return (
                            <Box
                                key={asset._id || asset.id || asset.name}
                                sx={{
                                    border: '1px solid white',
                                    borderRadius: 1,
                                    p: 1,
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                    <Typography variant="body1">{asset.name}</Typography>

                                    {isReleaseProduct ? (
                                        <Chip
                                            size="small"
                                            label={asset.release}
                                            sx={{
                                                bgcolor: colors.blueAccent[700],
                                                color: colors.grey[100],
                                            }}
                                        />
                                    ) : null}
                                </Box>

                                {isClickable && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        sx={{ ml: 2 }}
                                        onClick={() => handleEnableAsset(asset)}
                                    >
                                        Enable
                                    </Button>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {sortedAssets.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                        No additional external assets to enable.
                    </Typography>
                )}
            </>
        );
    };



    return (
        <Paper
            elevation={3}
            sx={{
                background: `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[900]})`,
                borderRadius: '20px',
                padding: '20px',
                marginBottom: '20px',
                position: 'relative'
            }}
        >
            {/* 2-column layout: “current products” on the left, “available external assets” on the right */}
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>

                {/* LEFT SIDE: Current Products */}
                <Box
                    flex="1"
                    sx={{
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '8px',
                        p: 2
                    }}
                >
                    {renderCurrentProducts()}
                </Box>

                {/* RIGHT SIDE: External Data Assets (not already owned) */}
                <Box
                    flex="2"
                    sx={{
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '8px',
                        p: 2
                    }}
                >
                    {renderAvailableAssets()}
                </Box>
            </Box>

            {/* Cookie Sync Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'visible',
                        borderRadius: '20px',
                    }
                }}
            >
                <DialogContent sx={{ padding: 0 }}>
                    <CookieSyncOnboarding organization={organization} onComplete={handleDialogClose} />
                </DialogContent>
            </Dialog>
            {/* API Onboarding Dialog */}
            <Dialog
                open={openApiDialog}
                onClose={closeApi}
                PaperProps={{
                    sx: { backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible', borderRadius: '20px' }
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <ApiOnboarding organization={organization} onComplete={closeApi} />
                </DialogContent>
            </Dialog>

            {/* CB Onboarding Dialog */}
            <Dialog
                open={openCbDialog}
                onClose={closeCb}
                PaperProps={{
                    sx: { backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible', borderRadius: '20px' }
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <CBOnboarding organization={organization} onComplete={closeCb} />
                </DialogContent>
            </Dialog>
            <EnableReleaseProductDialog
                open={releaseProductDialogOpen}
                onClose={closeReleaseProductDialog}
                onConfirm={handleConfirmEnableReleaseProduct}
                asset={selectedReleaseAsset}
                organization={organization}
            />
            <ManageReleaseProduct
                open={manageReleaseProductOpen}
                onClose={closeManageReleaseProduct}
                onDisabled={handleDisabledReleaseProduct}
                asset={selectedManageAsset}
                productName={selectedManageProductName}
                organization={organization}
                organizationId={organization?.organization_id}
            />
        </Paper>
    );
};

export default ProductOverview;
